import type { Asset, SubjectCanvasNode } from '@/types'
import { isAudioAsset, isCameraMotionControlAsset, isSetAsset } from '@/lib/assetUtils'
import { parseStackMemberId } from '@/lib/stackMembers'
import {
  AUDIO_NODE_WIDTH,
  AUDIO_NODE_HEIGHT,
  AUDIO_CONTENT_HEIGHT,
  SET_NODE_WIDTH,
  SET_NODE_HEIGHT,
  SET_THUMBNAIL_HEIGHT,
  DEFAULT_ASPECT_RATIO,
  calculateNodeDimensions,
  parseAspectRatio,
  type NodeDimensions,
} from './canvasConstants'

/** Subject cards render as a fixed 113×113 square — see `subjectToEngineNode`. */
export const SUBJECT_CARD_DIMENSION = 113

const SUBJECT_DIMENSIONS: NodeDimensions = {
  width: SUBJECT_CARD_DIMENSION,
  height: SUBJECT_CARD_DIMENSION,
  thumbnailWidth: SUBJECT_CARD_DIMENSION,
  thumbnailHeight: SUBJECT_CARD_DIMENSION,
}

/**
 * Kind-aware card dimensions for an asset on the canvas.
 *
 * Image / video assets use aspect-ratio-based sizing (`calculateNodeDimensions`).
 * Audio nodes use the fixed "hotdog" geometry (wider, shorter); set nodes use
 * the fixed large-card geometry (~3× wider and taller than image cards). The
 * stack row layout and the backdrop bounds need this so members of mixed-kind
 * stacks don't overlap (audio) or get over-spaced (set).
 */
export function getAssetCardDimensions(asset?: Asset | null): NodeDimensions {
  if (asset) {
    if (isAudioAsset(asset)) {
      return {
        width: AUDIO_NODE_WIDTH,
        height: AUDIO_NODE_HEIGHT,
        thumbnailWidth: AUDIO_NODE_WIDTH,
        thumbnailHeight: AUDIO_NODE_HEIGHT,
      }
    }
    if (isSetAsset(asset) || isCameraMotionControlAsset(asset)) {
      return {
        width: SET_NODE_WIDTH,
        height: Math.round(SET_NODE_HEIGHT),
        thumbnailWidth: SET_NODE_WIDTH,
        thumbnailHeight: Math.round(SET_NODE_HEIGHT),
      }
    }
  }
  const ar =
    asset?.width && asset?.height
      ? asset.width / asset.height
      : asset?.aspectRatio
        ? parseAspectRatio(asset.aspectRatio)
        : DEFAULT_ASPECT_RATIO
  return calculateNodeDimensions(ar)
}

/** Subject card dimensions — fixed square geometry. */
export function getSubjectCardDimensions(subject?: SubjectCanvasNode | null): NodeDimensions {
  void subject
  return SUBJECT_DIMENSIONS
}

/**
 * Resolve card dimensions for a stack member by prefixed node ID.
 *
 * Branches on `parseStackMemberId(memberNodeId)` so the same slot-layout
 * code can size both asset members (aspect-ratio-driven, audio/set special
 * cases) and subject members (fixed square) in mixed-kind stacks.
 */
export function getMemberCardDimensions(
  memberNodeId: string,
  assets: ReadonlyMap<string, Asset>,
  subjects: ReadonlyMap<string, SubjectCanvasNode>,
): NodeDimensions {
  const parsed = parseStackMemberId(memberNodeId)
  if (!parsed) return getAssetCardDimensions(undefined)
  if (parsed.kind === 'subject') return getSubjectCardDimensions(subjects.get(parsed.id))
  return getAssetCardDimensions(assets.get(parsed.id))
}

/**
 * Engine-box height for a stack member — the actual rendered rectangle the
 * Pixi engine uses for hit-testing and laying out node children.
 *
 * Asset / job nodes render their chrome (top strip, bottom strip) OUTSIDE
 * the engine box via `BaseAssetNodeView`'s gutter system, so the box is
 * just the thumbnail / body region. `getAssetCardDimensions` returns
 * `dims.height = TOP_BAR_HEIGHT + thumbnailHeight` (the V2 card height
 * including a virtual top bar inside) — using that for slot layout makes
 * mixed-kind rows misalign by 22 px because the engine box is smaller
 * than the layout thinks.
 *
 * For audio and set kinds `getAssetCardDimensions` happens to return
 * `thumbnailHeight === height` (the V2 quirk that lets the closed-stack
 * synth use thumbnailHeight to skip chrome); the actual engine box for
 * those is `AUDIO_CONTENT_HEIGHT` / `SET_THUMBNAIL_HEIGHT`. We special-case
 * them here without touching `getAssetCardDimensions`'s public contract.
 */
export function getMemberEngineBoxHeight(
  memberNodeId: string,
  assets: ReadonlyMap<string, Asset>,
  subjects: ReadonlyMap<string, SubjectCanvasNode>,
): number {
  void subjects
  const parsed = parseStackMemberId(memberNodeId)
  if (!parsed) return 0
  if (parsed.kind === 'subject') {
    // Subject card body is the full SUBJECT_DIMENSIONS — no chrome inside.
    return SUBJECT_DIMENSIONS.height
  }
  const asset = assets.get(parsed.id)
  if (asset) {
    if (isAudioAsset(asset)) return AUDIO_CONTENT_HEIGHT
    if (isSetAsset(asset) || isCameraMotionControlAsset(asset)) return Math.round(SET_THUMBNAIL_HEIGHT)
  }
  // Shot / image — `dims.thumbnailHeight` excludes the top bar already.
  return getAssetCardDimensions(asset ?? undefined).thumbnailHeight
}
